import React, { useState, useEffect } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import {
  Text,
  TextInput,
  Button,
  useTheme,
  HelperText,
} from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Coffee, Eye, EyeOff, CheckCircle } from 'lucide-react-native';
import { passwordResetSchema, PasswordResetData } from '@coffeeplexity/shared';
import { useResetPassword } from '@coffeeplexity/api-client';
import Toast from 'react-native-toast-message';

export function ResetPasswordScreen() {
  const theme = useTheme();
  const navigation = useNavigation();
  const route = useRoute();
  const resetPasswordMutation = useResetPassword();
  const [showPassword, setShowPassword] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    // Get token from route params or deep link
    const tokenParam = route.params?.token;
    if (tokenParam) {
      setToken(tokenParam);
    } else {
      // Redirect to forgot password if no token
      navigation.navigate('forgotPassword' as never);
    }
  }, [route.params, navigation]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<PasswordResetData>({
    resolver: zodResolver(passwordResetSchema),
    defaultValues: {
      token: token || '',
      new_password: '',
    },
  });

  const onSubmit = async (data: PasswordResetData) => {
    if (!token) {
      setError('root', { message: 'Invalid reset token' });
      return;
    }

    try {
      await resetPasswordMutation.mutateAsync({
        token,
        newPassword: data.new_password,
      });
      setResetSuccess(true);
      Toast.show({
        type: 'success',
        text1: 'Password Reset Successful',
        text2: 'You can now log in with your new password.',
      });
    } catch (error: any) {
      let errorMessage = 'An error occurred. Please try again.';

      if (error.status === 400) {
        errorMessage = 'Invalid or expired reset token';
      } else if (error.status === 422) {
        errorMessage = 'Please check your password and try again';
      }

      Toast.show({
        type: 'error',
        text1: 'Reset Failed',
        text2: errorMessage,
      });
      setError('root', { message: errorMessage });
    }
  };

  if (resetSuccess) {
    return (
      <SafeAreaView
        style={[styles.container, { backgroundColor: theme.colors.background }]}
      >
        <View style={styles.successContainer}>
          <CheckCircle size={64} color={theme.colors.primary} />
          <Text
            variant='headlineLarge'
            style={[styles.title, { color: theme.colors.onBackground }]}
          >
            Password Reset Successful
          </Text>
          <Text
            variant='bodyLarge'
            style={[styles.subtitle, { color: theme.colors.onSurfaceVariant }]}
          >
            Your password has been successfully reset. You can now log in with
            your new password.
          </Text>
          <Button
            mode='contained'
            onPress={() => navigation.navigate('login' as never)}
            style={[styles.button, { backgroundColor: theme.colors.primary }]}
            contentStyle={styles.buttonContent}
          >
            Go to Login
          </Button>
        </View>
      </SafeAreaView>
    );
  }

  if (!token) {
    return (
      <SafeAreaView
        style={[styles.container, { backgroundColor: theme.colors.background }]}
      >
        <View style={styles.errorContainer}>
          <Coffee size={64} color={theme.colors.primary} />
          <Text
            variant='headlineLarge'
            style={[styles.title, { color: theme.colors.onBackground }]}
          >
            Invalid Reset Link
          </Text>
          <Text
            variant='bodyLarge'
            style={[styles.subtitle, { color: theme.colors.onSurfaceVariant }]}
          >
            This password reset link is invalid or has expired.
          </Text>
          <Button
            mode='contained'
            onPress={() => navigation.navigate('forgotPassword' as never)}
            style={[styles.button, { backgroundColor: theme.colors.primary }]}
            contentStyle={styles.buttonContent}
          >
            Request New Reset Link
          </Button>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
    >
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardAvoidingView}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          {/* Header */}
          <View style={styles.header}>
            <Coffee size={64} color={theme.colors.primary} />
            <Text
              variant='headlineLarge'
              style={[styles.title, { color: theme.colors.onBackground }]}
            >
              Reset Your Password
            </Text>
            <Text
              variant='bodyLarge'
              style={[
                styles.subtitle,
                { color: theme.colors.onSurfaceVariant },
              ]}
            >
              Enter your new password below
            </Text>
          </View>

          {/* Form */}
          <View style={styles.form}>
            <Controller
              control={control}
              name='new_password'
              render={({ field: { onChange, onBlur, value } }) => (
                <View style={styles.inputContainer}>
                  <TextInput
                    label='New Password'
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    secureTextEntry={!showPassword}
                    autoComplete='new-password'
                    error={!!errors.new_password}
                    style={styles.input}
                    mode='outlined'
                    right={
                      <TextInput.Icon
                        icon={showPassword ? EyeOff : Eye}
                        onPress={() => setShowPassword(!showPassword)}
                      />
                    }
                  />
                  {errors.new_password && (
                    <HelperText type='error' visible={!!errors.new_password}>
                      {errors.new_password.message}
                    </HelperText>
                  )}
                </View>
              )}
            />

            {/* Password Requirements */}
            <View style={styles.requirementsContainer}>
              <Text
                variant='bodySmall'
                style={[
                  styles.requirementsTitle,
                  { color: theme.colors.onSurfaceVariant },
                ]}
              >
                Password must contain:
              </Text>
              <Text
                variant='bodySmall'
                style={[
                  styles.requirement,
                  { color: theme.colors.onSurfaceVariant },
                ]}
              >
                • At least 8 characters
              </Text>
              <Text
                variant='bodySmall'
                style={[
                  styles.requirement,
                  { color: theme.colors.onSurfaceVariant },
                ]}
              >
                • One uppercase letter
              </Text>
              <Text
                variant='bodySmall'
                style={[
                  styles.requirement,
                  { color: theme.colors.onSurfaceVariant },
                ]}
              >
                • One lowercase letter
              </Text>
              <Text
                variant='bodySmall'
                style={[
                  styles.requirement,
                  { color: theme.colors.onSurfaceVariant },
                ]}
              >
                • One number
              </Text>
            </View>

            {/* Error Message */}
            {errors.root && (
              <View
                style={[
                  styles.errorMessage,
                  { backgroundColor: theme.colors.errorContainer },
                ]}
              >
                <Text
                  style={[
                    styles.errorText,
                    { color: theme.colors.onErrorContainer },
                  ]}
                >
                  {errors.root.message}
                </Text>
              </View>
            )}

            {/* Reset Button */}
            <Button
              mode='contained'
              onPress={handleSubmit(onSubmit)}
              loading={resetPasswordMutation.isPending}
              disabled={resetPasswordMutation.isPending}
              style={[styles.button, { backgroundColor: theme.colors.primary }]}
              contentStyle={styles.buttonContent}
            >
              Reset Password
            </Button>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    padding: 24,
  },
  header: {
    alignItems: 'center',
    marginTop: 40,
    marginBottom: 48,
  },
  title: {
    marginTop: 16,
    marginBottom: 8,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  subtitle: {
    textAlign: 'center',
  },
  form: {
    flex: 1,
  },
  inputContainer: {
    marginBottom: 16,
  },
  input: {
    backgroundColor: 'transparent',
  },
  requirementsContainer: {
    marginBottom: 24,
    padding: 16,
    borderRadius: 8,
    backgroundColor: 'rgba(0,0,0,0.05)',
  },
  requirementsTitle: {
    marginBottom: 8,
    fontWeight: '500',
  },
  requirement: {
    marginBottom: 4,
  },
  errorMessage: {
    padding: 12,
    borderRadius: 8,
    marginBottom: 16,
  },
  errorText: {
    fontSize: 14,
  },
  button: {
    marginBottom: 16,
  },
  buttonContent: {
    height: 48,
  },
  successContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
});
